import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { SignInButton, SignUpButton, UserButton, useClerk, useUser } from "@clerk/react";
import { ArrowLeft, Loader2, Palette } from "lucide-react";
import { LibraryAppearancePage } from "./AccountAppearance";
import { fontOptions, type GalleryTheme } from "./model";
import { hasSessionHint } from "./session";

export function ThemedShell({ theme, children, className = "" }: { theme: GalleryTheme; children: ReactNode; className?: string }) {
  const font = fontOptions.find((option) => option.value === theme.fontFamily)?.css || fontOptions[0].css;
  return (
    <main
      className={`app-shell ${className}`.trim()}
      style={{
        "--bg": theme.background,
        "--fg": theme.foreground,
        "--accent": theme.accent,
        "--font": font,
        "--fit": theme.imageFit
      } as CSSProperties}
    >
      {children}
    </main>
  );
}

export function AuthControls() {
  const { isLoaded, isSignedIn } = useUser();
  const clerk = useClerk();
  if (!isLoaded && hasSessionHint()) return <div className="auth-controls auth-controls-loading" aria-hidden="true" />;
  if (isSignedIn) return <div className="auth-controls signed-in"><UserButton>
    <UserButton.UserProfilePage label="account" />
    <UserButton.UserProfilePage label="security" />
    <UserButton.UserProfilePage label="Library Appearance" url="library-appearance" labelIcon={<Palette size={16} />}>
      <LibraryAppearancePage />
    </UserButton.UserProfilePage>
  </UserButton></div>;
  // Clerk queues these calls until it has loaded, so the buttons work from the first paint.
  return (
    <div className="auth-controls">
      <button className="secondary" type="button" onClick={() => { void clerk.openSignIn(); }}>Sign in</button>
      <button type="button" onClick={() => { void clerk.openSignUp(); }}>Sign up</button>
    </div>
  );
}

export function ToolTopbar({ slug, children }: { slug: string; children?: ReactNode }) {
  return (
    <header className="topbar">
      <a href={`/g/${slug}`} className="back-link"><ArrowLeft size={17} />Gallery</a>
      <div className="topbar-actions">{children}<AuthControls /></div>
    </header>
  );
}

export function LoadingView() {
  return <main className="center-shell"><Loader2 className="spin" size={24} /></main>;
}

export function ErrorView({ message }: { message: string }) {
  return <main className="center-shell"><p className="error">{message}</p><a className="link-button" href="/">Home</a></main>;
}

export function AccessDeniedView({ slug }: { slug: string }) {
  return (
    <main className="center-shell">
      <section className="auth-required">
        <p className="eyebrow">Owner access</p>
        <h1>This gallery belongs to another account.</h1>
        <p className="intro compact">You can view the public gallery, but its uploads and design are private owner tools.</p>
        <a className="link-button" href={`/g/${slug}`}>View gallery</a>
      </section>
    </main>
  );
}

export function RequireSignInView({ title }: { title: string }) {
  return (
    <main className="center-shell">
      <section className="auth-required">
        <p className="eyebrow">Authentication</p>
        <h1>{title}</h1>
        <p className="intro compact">Public galleries stay viewable by link. Owner tools require your account.</p>
        <div className="actions">
          <SignInButton mode="modal"><button type="button">Sign in</button></SignInButton>
          <SignUpButton mode="modal"><button className="secondary" type="button">Create account</button></SignUpButton>
        </div>
      </section>
    </main>
  );
}

// A styled stand-in for window.confirm, kept for actions that cannot be undone.
export function ConfirmDialog({ open, title, message, confirmLabel, onConfirm, onCancel }: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal?.();
    if (!open && element.open) element.close();
  }, [open]);
  return (
    <dialog ref={dialog} className="confirm-dialog" aria-labelledby="confirm-dialog-title" onCancel={(event) => { event.preventDefault(); onCancel(); }}>
      <h2 id="confirm-dialog-title">{title}</h2>
      <p>{message}</p>
      <div className="confirm-dialog-actions">
        <button type="button" className="secondary" autoFocus onClick={onCancel}>Cancel</button>
        <button type="button" onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </dialog>
  );
}
